import type { TranslationMap } from "../lib/types.ts";

export const zh_TW: TranslationMap = {
  common: {
    health: "健康狀況",
    ok: "正常",
    online: "在線",
    offline: "離線",
    connect: "連接",
    refresh: "刷新",
    enabled: "已啟用",
    disabled: "已禁用",
    na: "不適用",
    version: "版本",
    docs: "文檔",
    resources: "資源",
    search: "搜尋",
  },
  nav: {
    chat: "聊天",
    control: "控制",
    agent: "代理",
    settings: "設置",
    expand: "展開側邊欄",
    collapse: "折疊側邊欄",
    resize: "調整側邊欄大小",
  },
  tabs: {
    agents: "代理",
    overview: "概覽",
    channels: "頻道",
    instances: "實例",
    sessions: "會話",
    cron: "定時任務",
    nodes: "節點",
    chat: "聊天",
    config: "配置",
    communications: "通訊",
    appearance: "外觀與設置",
    automation: "自動化",
    infrastructure: "基礎設施",
    aiAgents: "AI 與代理",
    debug: "調試",
    logs: "日誌",
  },
  subtitles: {
    agents: "工作區、工具、身份。",
    overview: "狀態、入口點、健康。",
    channels: "頻道和設置。",
    instances: "已連接客戶端和節點。",
    sessions: "活動會話和默認設置。",
    cron: "喚醒和重複運行。",
    nodes: "配對設備和命令。",
    chat: "網關聊天，快速干預。",
    config: "編輯 remoteclaw.json。",
    communications: "頻道、消息和音頻設置。",
    appearance: "主題、界面和設置向導設置。",
    automation: "命令、鉤子、定時任務和插件設置。",
    infrastructure: "網關、Web、瀏覽器和媒體設置。",
    aiAgents: "代理、模型、技能、工具、記憶和會話設置。",
    debug: "快照、事件、RPC。",
    logs: "實時網關日誌。",
  },
  overview: {
    access: {
      title: "網關訪問",
      subtitle: "儀表板連接的位置及其身份驗證方式。",
      wsUrl: "WebSocket URL",
      token: "網關令牌",
      password: "密碼 (不存儲)",
      sessionKey: "默認會話密鑰",
      language: "語言",
      connectHint: "點擊連接以應用連接更改。",
      trustedProxy: "通過受信任代理身份驗證。",
    },
    snapshot: {
      title: "快照",
      subtitle: "最新的網關握手信息。",
      status: "狀態",
      uptime: "運行時間",
      tickInterval: "刻度間隔",
      lastChannelsRefresh: "最後頻道刷新",
      channelsHint: "使用頻道鏈接 WhatsApp、Telegram、Discord、Signal 或 iMessage。",
    },
    stats: {
      instances: "實例",
      instancesHint: "過去 5 分鐘內的在線信號。",
      sessions: "會話",
      sessionsHint: "網關跟蹤的最近會話密鑰。",
      cron: "定時任務",
      cronNext: "下次喚醒 {time}",
    },
    notes: {
      title: "備註",
      subtitle: "遠程控制設置的快速提醒。",
      tailscaleTitle: "Tailscale serve",
      tailscaleText: "首選 serve 模式以通過 tailnet 身份驗證將網關保持在回環地址。",
      sessionTitle: "會話清理",
      sessionText: "使用 /new 或 sessions.patch 重置上下文。",
      cronTitle: "定時任務提醒",
      cronText: "為重複運行使用隔離的會話。",
    },
    auth: {
      required: "此網關需要身份驗證。添加令牌或密碼，然後點擊連接。",
      failed: "身份驗證失敗。請使用 {command} 重新複製令牌化 URL，或更新令牌，然後點擊連接。",
    },
    pairing: {
      hint: "此裝置需要閘道主機的配對批准。",
      mobileHint:
        "在手機上？從桌面執行 remoteclaw dashboard --no-open 複製完整 URL（包括 #token=...）。",
    },
    insecure: {
      hint: "此頁面為 HTTP，因此瀏覽器阻止設備標識。請使用 HTTPS (Tailscale Serve) 或在網關主機上打開 {url}。",
      stayHttp: "如果您必須保持 HTTP，請設置 {config} (僅限令牌)。",
    },
    connection: {
      title: "如何連接",
      step1: "在主機上啟動閘道：",
      step2: "取得帶令牌的儀表板 URL：",
      step3: "將 WebSocket URL 和令牌貼到上方，或直接開啟帶令牌的 URL。",
      step4: "或產生可重複使用的令牌：",
      docsHint: "如需遠端存取，建議使用 Tailscale Serve。",
      docsLink: "查看文件 →",
    },
    cards: {
      cost: "費用",
      recentSessions: "最近會話",
    },
    attention: {
      title: "注意事項",
    },
    eventLog: {
      title: "事件日誌",
    },
    logTail: {
      title: "閘道日誌",
    },
    quickActions: {
      newSession: "新建會話",
      automation: "自動化",
      refreshAll: "全部刷新",
      terminal: "終端",
    },
    palette: {
      placeholder: "輸入指令…",
      noResults: "無結果",
    },
  },
  login: {
    subtitle: "閘道儀表板",
    passwordPlaceholder: "可選",
  },
  chat: {
    disconnected: "已斷開與網關的連接。",
    refreshTitle: "刷新聊天數據",
    thinkingToggle: "切換助手思考/工作輸出",
    focusToggle: "切換專注模式 (隱藏側邊欄 + 頁面頁眉)",
    hideCronSessions: "隱藏定時任務會話",
    showCronSessions: "顯示定時任務會話",
    showCronSessionsHidden: "顯示定時任務會話 (已隱藏 {count} 個)",
    onboardingDisabled: "引導期間禁用",
  },
  languages: {
    en: "English",
    zhCN: "简体中文 (簡體中文)",
    zhTW: "繁體中文 (繁體中文)",
    ptBR: "Português (巴西葡萄牙語)",
    de: "Deutsch (德語)",
    es: "Español (西班牙語)",
  },
};
